/**
 * Project Profitability Table Component
 * Phase 6 Batch 3 - EOS Add-on Reports
 *
 * Table showing project profitability analysis with health status
 */
import { useProjectProfitability, type ProjectProfitabilityRow } from '@/hooks/useDashboardReports';
import { AlertCircle, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { useState } from 'react';

export function ProjectProfitabilityTable() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const { data, isLoading, error } = useProjectProfitability(statusFilter);
  const [sortBy, setSortBy] = useState<'margin' | 'invoiced' | 'collected'>('margin');

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4 animate-pulse"></div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <AlertCircle className="inline mr-2" size={20} />
        Failed to load project profitability data
      </div>
    );
  }

  const profitData = data as { success: boolean; data: ProjectProfitabilityRow[]; summary_by_stage: any };
  const projects = profitData?.data || [];
  const summaryByStage = profitData?.summary_by_stage || {};

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getHealthIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <TrendingUp className="text-emerald-600" size={18} />;
      case 'at_risk':
        return <AlertTriangle className="text-yellow-600" size={18} />;
      case 'over_budget':
        return <TrendingDown className="text-red-600" size={18} />;
      default:
        return null;
    }
  };

  const getHealthLabel = (status: string) => {
    switch (status) {
      case 'healthy':
        return <span className="text-emerald-700 font-medium">Healthy</span>;
      case 'at_risk':
        return <span className="text-yellow-700 font-medium">At Risk</span>;
      case 'over_budget':
        return <span className="text-red-700 font-medium">Over Budget</span>;
      default:
        return status;
    }
  };

  // Sort projects
  const sortedProjects = [...projects].sort((a, b) => {
    if (sortBy === 'margin') {
      const marginA = a.confirmed_fees > 0 ? (a.collected_amount / a.confirmed_fees) * 100 : 0;
      const marginB = b.confirmed_fees > 0 ? (b.collected_amount / b.confirmed_fees) * 100 : 0;
      return marginB - marginA;
    } else if (sortBy === 'invoiced') {
      return b.invoiced_percentage - a.invoiced_percentage;
    } else {
      return b.collection_rate - a.collection_rate;
    }
  });

  return (
    <div className="bg-white p-6 rounded-lg shadow" data-testid="project-profitability-table">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Project Profitability
        </h2>
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded text-sm"
          >
            <option value="all">All Projects</option>
            <option value="active">Active Only</option>
            <option value="completed">Completed Only</option>
          </select>
          <div className="flex gap-1 text-sm">
            <button
              onClick={() => setSortBy('margin')}
              className={`px-3 py-1 rounded ${
                sortBy === 'margin' ? 'bg-blue-100 text-sky-700' : 'bg-gray-100 text-gray-700'
              }`}
            >
              By Margin
            </button>
            <button
              onClick={() => setSortBy('invoiced')}
              className={`px-3 py-1 rounded ${
                sortBy === 'invoiced' ? 'bg-blue-100 text-sky-700' : 'bg-gray-100 text-gray-700'
              }`}
            >
              By Invoiced %
            </button>
            <button
              onClick={() => setSortBy('collected')}
              className={`px-3 py-1 rounded ${
                sortBy === 'collected' ? 'bg-blue-100 text-sky-700' : 'bg-gray-100 text-gray-700'
              }`}
            >
              By Collection
            </button>
          </div>
        </div>
      </div>

      {/* Summary by Stage */}
      {Object.keys(summaryByStage).length > 0 && (
        <div className="mb-6 grid grid-cols-4 gap-4">
          {Object.entries(summaryByStage).map(([stage, summary]: [string, any]) => (
            <div key={stage} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="text-xs text-gray-600 mb-1 capitalize">
                {stage.replace(/_/g, ' ')}
              </div>
              <div className="text-lg font-semibold text-gray-900">
                {summary.project_count} projects
              </div>
              <div className="text-sm text-gray-700 mt-1">
                {summary.avg_collection_rate.toFixed(1)}% collected
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Project
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Client
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Confirmed Fees
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Invoiced %
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Collection Rate
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Unbilled
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                Health
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedProjects.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  No projects found
                </td>
              </tr>
            ) : (
              sortedProjects.map((project) => (
                <tr key={project.project_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {project.project_number ? `P-${project.project_number}` : 'Proposal'}
                    </div>
                    <div className="text-xs text-gray-500 truncate max-w-xs">
                      {project.project_name}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {project.client_name}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900">
                    {formatCurrency(project.confirmed_fees)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                    <span
                      className={
                        project.invoiced_percentage >= 80
                          ? 'text-emerald-600 font-medium'
                          : project.invoiced_percentage >= 50
                          ? 'text-yellow-600'
                          : 'text-gray-600'
                      }
                    >
                      {project.invoiced_percentage.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                    <span
                      className={
                        project.collection_rate >= 80
                          ? 'text-emerald-600 font-medium'
                          : project.collection_rate >= 50
                          ? 'text-yellow-600'
                          : 'text-red-600'
                      }
                    >
                      {project.collection_rate.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900">
                    {formatCurrency(project.unbilled_amount)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center gap-1">
                      {getHealthIcon(project.health_status)}
                      <span className="text-xs">
                        {getHealthLabel(project.health_status)}
                      </span>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Totals Footer */}
      {projects.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Total Projects:</span>
            <span className="ml-2 font-semibold">{projects.length}</span>
          </div>
          <div>
            <span className="text-gray-600">Total Confirmed:</span>
            <span className="ml-2 font-semibold">
              {formatCurrency(projects.reduce((sum, p) => sum + p.confirmed_fees, 0))}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Total Invoiced:</span>
            <span className="ml-2 font-semibold text-sky-600">
              {formatCurrency(projects.reduce((sum, p) => sum + p.invoiced_amount, 0))}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Total Unbilled:</span>
            <span className="ml-2 font-semibold text-slate-600">
              {formatCurrency(projects.reduce((sum, p) => sum + p.unbilled_amount, 0))}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
